import { DatePicker } from '@progress/kendo-react-dateinputs'
import { sizedUrl, isHostedImage } from 'app/utils'
import { copyToClipboard } from 'generic/utility'
import { ImageUploadError } from 'components/common/constants'
import { LoadSegmentCodes } from 'components/common/load-partner-codes/component'
import { DropdownComponent } from 'components/dropdown/component'
import { FrameLandscapeIcon } from 'components/icons/frame-landscape'
import { Loading, SIZES } from 'components/loading'
import * as Validation from 'components/validation/validations'
import { RasaContext } from 'context'
import { Dataset } from 'generic/dataset'
import { BaseClientEntity } from 'generic/baseClientEntity'
import { isFalsey } from 'generic/utility'
import * as React from 'react'
import { DATEPICKER_DATE_FORMAT, Roles } from 'shared/constants'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import { DEFAULT_COMMUNITY_PARTNER_CODES, ScheduledContentTypes } from 'shared_server_client/constants'
import { endOfDay,  startOfDay } from 'shared_server_client/dates'
import { isCustomTemplate, isStripoTemplate, needsImageUpload } from 'shared_server_client/utils'
import {
  ClearScheduledContentDataState,
  IMAGE_MAX_WIDTH,
  ImageDisplayType,
  ImageUploadType,
  InitialScheduledContentModalState,
  SCHEDULED_CONTENT_NOT_ACTIVE_MESSAGE
} from '../constants'
import { ScheduleContentModalBaseComponent, ScheduleContentModalProps } from './base'
import { ImageSelectorModal } from '../../../common/image-selector/modal';
import { BillingPlanDetailCode } from '../../../../shared_server_client/types/billing_plan';
import './scheduledImage.scss';
import { Input } from 'reactstrap'


export class ScheduledImageModalComponent extends ScheduleContentModalBaseComponent {
  public static key: string = 'scheduledImageModal'
  public static contextType = RasaContext
  private sharedStore: SharedStore
  private communityId: string = null
  public fileInputRef;

  constructor(props: ScheduleContentModalProps) {
    super(props, ScheduledImageModalComponent.key)
    this.state = {
      ...InitialScheduledContentModalState,
      showImageUploadButtons: !props.data[ScheduledImageModalComponent.key].data.imageUrl,
      showImageSelectorModal: false,
      showCopyButton: false,
    }
    this.fileInputRef = React.createRef()
  }

  public componentDidUpdate(prevProps: Readonly<ScheduleContentModalProps>) {
    if (prevProps.data[ScheduledImageModalComponent.key].data.imageUrl !== this.props.data[ScheduledImageModalComponent.key].data.imageUrl) {
      this.setState({ showImageUploadButtons: !this.props.data[ScheduledImageModalComponent.key].data.imageUrl })
    }
  }

  public componentDidMount() {
    this.sharedStore = SharedStore.instance(this.context)
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
      this.sharedStore.getValue(SharedKeys.role),
    ]).then(([activeCommunity, role]) => {
      this.communityId = activeCommunity.communityId
      const galleryFeature: any = activeCommunity.billingInfo.currentPlan.details.find((x) =>
        x.billing_plan_code === BillingPlanDetailCode.IMAGE_GALLERY)
      this.setState({
        hasImageGalleryAccess: galleryFeature && galleryFeature.limit_val !== 0,
        isSuperUser: role === Roles.super_admin,
      })

      // Load community ad sources
      new Dataset().loadCommunityDataset('communityAdSources', this.communityId)
        .then((adSources) => {
          const transformedAdSources = (adSources[0] || []).map((source: any) => ({
            key: source.id,
            description: source.name
          }))

          // Add "None" option at the beginning
          const adSourcesWithNone = [
            { key: -1, description: 'None' },
            ...transformedAdSources
          ]

          this.setState({ communityAdSources: adSourcesWithNone })
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to load community ad sources:', error)
        })
    })
  }

  public renderChildren(data) {
    const { endDate, imageUrl, linkUrl, location, startDate, imageTitle, imageAltText } = data

    return this.state.isLoading || this.state.isSaving
    ? <Loading size={SIZES.LARGE}/>
    : <div className="scheduled-image-modal-body">
        <div className="body-container">
          <div className="left-side-container">
            <div className="third-row-container">
              <h5>{'Title'}</h5>
              <div className="url-input-container">
                <Input
                    disabled={this.shouldNameDisabled(data)}
                    type="text"
                    value={imageTitle ? imageTitle : ''}
                    onChange={(e) => this.updateProperty('imageTitle', e.target.value)}/>
              </div>
            </div>
            <div className="third-row-container">
              <h5>{'Alt Text'}</h5>
              <div className="url-input-container">
                <Input
                    type="text"
                    value={imageAltText}
                    onChange={(e) => this.updateProperty('imageAltText', e.target.value)}/>
              </div>
            </div>
            <div className="second-row-container">
              <h5>Location</h5>
              <DropdownComponent
                  data={this.props.locationOptions}
                  selected={location ? location.key : ''}
                  onChange={this.changeLocation}/>
            </div>
            <div className="third-row-container">
              <h5>{'Link Url (call to action)'}</h5>
              <div className="url-input-container">
                <Input
                    type="text"
                    value={linkUrl ? linkUrl : ''}
                    onChange={(e) => this.updateProperty('linkUrl', e.target.value)}
                    invalid={linkUrl && !Validation.validateOptionalUrlOrMailto(linkUrl.trim()).valid}/>
                {linkUrl && !Validation.validateOptionalUrlOrMailto(linkUrl.trim()).valid && (
                  <div className="text-danger small mt-1">
                    Must be a valid URL or mailto link
                  </div>
                )}
              </div>
            </div>
            <div className="fourth-row-container">
              <div className="start-date-container">
                <h5>Start Date</h5>
                <DatePicker
                    className="modal-schedule-date-picker"
                    format={DATEPICKER_DATE_FORMAT}
                    value={startDate}
                    onChange={(e) => this.updateProperty('startDate', e.target.value)}
                />
              </div>
              <div className="end-date-container">
                <h5>End Date</h5>
                <DatePicker
                    className="modal-schedule-date-picker"
                    format={DATEPICKER_DATE_FORMAT}
                    value={endDate}
                    min={new Date()}
                    onChange={(e) => this.updateProperty('endDate', e.target.value)}
                />
              </div>
            </div>
            <div className="fifth-row-container">
              {this.state.communityAdSources && this.state.communityAdSources.length > 1 && (
                <div className="ad-source-container">
                  <h5>Ad Source</h5>
                  <DropdownComponent
                      data={this.state.communityAdSources}
                      selected={data.community_ad_source_id == null || data.community_ad_source_id === -1 ? -1 : data.community_ad_source_id}
                      onChange={this.changeAdSource}/>
                </div>
              )}
              <div className="segment-code-container">
                <LoadSegmentCodes
                    label="Segment"
                    setSegmentData={this.setSegmentData}
                    segmentCode={data.community_partner_code}
                    setSegmentCode={this.setSegmentCode}/>
              </div>
            </div>
            {
                this.state.modalErrorMessage &&
                <div className="error-container">
                  {this.state.modalErrorMessage}
                </div>
            }
            {
                this.state.scheduleErrorMessage &&
                <div className="error-container">
                  {this.state.scheduleErrorMessage}
                  <a onClick={() => this.enableScheduleContent(this.getSelectedLocation(), this.communityId)} href="#">Click
                    here to toggle it on.</a>
                </div>
            }
          </div>
          <div className="right-side-container">
            <h5>Preview</h5>
            <div className="preview-container">
              {
                imageUrl
                  ? (
                      <div>
                        <div className="scheduled-image-with-overlay" style={{position: 'relative', display: 'inline-block'}}>
                          <img src={sizedUrl(imageUrl, 250, 400)} onError={this.onImageError}/>
                          <div className="scheduled-image-overlay-icons" style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            display: 'flex',
                            gap: '8px',
                            zIndex: 10,
                            opacity: 0,
                            transition: 'opacity 0.3s ease'
                          }}>
                            <div
                              className="overlay-icon edit-icon"
                              onClick={this.openImageSelectorModal}
                              title="Change Image"
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(0, 123, 255, 0.9)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                              }}
                            >
                              <i className="fas fa-camera" style={{fontSize: '16px', color: 'white'}} />
                            </div>
                            <div
                              className="overlay-icon delete-icon"
                              onClick={this.onChangeClick}
                              title="Remove Image"
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(220, 53, 69, 0.9)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                              }}
                            >
                              <i className="fas fa-trash" style={{fontSize: '16px', color: 'white'}} />
                            </div>
                          </div>
                        </div>
                        {!isHostedImage(imageUrl) && (
                          <div
                            className="image-url-display"
                            style={{
                              marginTop: '0.5rem',
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <small
                              className="text-muted"
                              onClick={() => copyToClipboard(imageUrl)}
                              title="Click to copy URL"
                              style={{
                                fontSize: '0.75rem',
                                wordBreak: 'break-all',
                                maxWidth: '300px',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationStyle: 'dotted'
                              }}
                            >
                              {imageUrl}
                            </small>
                          </div>
                        )}
                      </div>
                    )
                  : (
                      <div className="scheduled-placeholder-with-overlay" style={{position: 'relative', display: 'inline-block'}}>
                        <div
                          onClick={this.openImageSelectorModal}
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '400px',
                            height: '250px',
                            backgroundColor: '#f8f9fa',
                            border: '2px dashed #dee2e6',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <FrameLandscapeIcon svgWidth={60} svgHeight={60}/>
                        </div>
                        <div className="scheduled-placeholder-overlay" style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          zIndex: 10,
                          opacity: 0,
                          transition: 'opacity 0.3s ease'
                        }}>
                          <div
                            className="overlay-icon edit-icon"
                            onClick={this.openImageSelectorModal}
                            title="Select Image"
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: 'rgba(0, 123, 255, 0.9)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                            }}
                          >
                            <i className="fas fa-camera" style={{fontSize: '16px', color: 'white'}} />
                          </div>
                        </div>
                      </div>
                    )
              }
              { (imageUrl) &&
                 this.getUploadErrorMsg(data)
              }
            </div>
          </div>
        </div>
        {this.state.showImageSelectorModal && (
          <ImageSelectorModal
            data={this.state.showImageSelectorModal ? {} : null}
            closeModal={this.closeImageSelectorModal}
            onSelectImage={this.handleImageSelection}
            saveToGallery={true}
            allowAIGeneration={true}
            allowUpload={true}
            allowURL={true}
            allowGallery={true}
            articleTitle={this.props.data[ScheduledImageModalComponent.key] && this.props.data[ScheduledImageModalComponent.key].data ? this.props.data[ScheduledImageModalComponent.key].data.imageTitle : ''}
            articleDescription={this.props.data[ScheduledImageModalComponent.key] && this.props.data[ScheduledImageModalComponent.key].data ? this.props.data[ScheduledImageModalComponent.key].data.imageDescription || this.props.data[ScheduledImageModalComponent.key].data.imageTitle : ''}
            contentType="scheduled-image"
            locationKey={this.props.data[ScheduledImageModalComponent.key] && this.props.data[ScheduledImageModalComponent.key].data && this.props.data[ScheduledImageModalComponent.key].data.location ? this.props.data[ScheduledImageModalComponent.key].data.location.key : ''}
            closeButtonText="Cancel"
            size="lg"
          />
        )}
      </div>
  }

  protected changeLocation = (e) => {
    this.updateProperty('location', e.selected)
    this.updateProperty('imageAltText', this.generateAltText(e.selected.key))
  }

  protected changeAdSource = (e) => {
    this.updateProperty('community_ad_source_id', e.selected.key)
  }

  protected getTitleText(data: any): string {
    return data.typeId
      ? 'Edit Scheduled Image'
      : (data.isCloneItem) ? 'Clone Scheduled Image' : 'Add Scheduled Image'
  }

  protected saveDisabled(data: any): boolean {
    const { endDate, id, imageUrl, location, startDate, imageAltText, linkUrl } = data
    const isMissingData = isFalsey(endDate && imageUrl && location && startDate && imageAltText !== '')
    const isInvalidLinkUrl = linkUrl && !Validation.validateOptionalUrlOrMailto(linkUrl.trim()).valid
    // no id => create image
    // id => update image
    return !id
    ? isMissingData || !Validation.validateUrl(imageUrl).valid || isInvalidLinkUrl
    : isMissingData || !this.state.isDirty || isInvalidLinkUrl
  }

  protected xButtonClick(data: any) {
    const currentIsSuperUser = this.state.isSuperUser
    this.setState({
      ...ClearScheduledContentDataState,
      isSuperUser: currentIsSuperUser,
    })
    this.props.closeModal(ScheduledImageModalComponent.key)
  }

  protected createOrUpdate(payload: any): Promise<any> {
    const selectedLocation = this.getSelectedLocation()
    const customTemplate = isCustomTemplate(this.props.emailLayout ? this.props.emailLayout.email_template : '')
    const stripoTemplate = isStripoTemplate(this.props.emailLayout ? this.props.emailLayout.email_template : '')
    if (!customTemplate && !stripoTemplate && !this.isScheduleContentEnabled(selectedLocation)) {
      this.setState({
        isSaving: false,
        scheduleErrorMessage: SCHEDULED_CONTENT_NOT_ACTIVE_MESSAGE,
      })
      return Promise.reject(SCHEDULED_CONTENT_NOT_ACTIVE_MESSAGE)
    }
    return this.context.entityMetadata
    .getEntityObject('scheduledContent', payload.communityId, null)
    .then((scheduledContentEntityObject: BaseClientEntity) => {
      const {endDate, imageTitle, imageAltText, imageUrl, imageHeight, imageWidth,
        linkUrl, startDate, typeId, community_ad_source_id} = payload
      scheduledContentEntityObject.setRecordId(payload.communityId, typeId && !payload.isCloneItem ? typeId : null)
      scheduledContentEntityObject.data.contentType = ScheduledContentTypes.image
      scheduledContentEntityObject.data.endDate = endOfDay(endDate, payload.timezone)
      scheduledContentEntityObject.data.startDate = startOfDay(startDate, payload.timezone)
      scheduledContentEntityObject.data.community_partner_code = payload.community_partner_code
      scheduledContentEntityObject.data.community_ad_source_id = community_ad_source_id
      scheduledContentEntityObject.data.imageUrl = imageUrl
      scheduledContentEntityObject.data.imageHeight = imageHeight
      scheduledContentEntityObject.data.imageWidth = imageWidth
      scheduledContentEntityObject.data.imageDisplayType = ImageDisplayType.FIXED
      scheduledContentEntityObject.data.linkUrl = linkUrl
      scheduledContentEntityObject.data.isClientUrl = needsImageUpload(imageUrl)
      scheduledContentEntityObject.data.location = selectedLocation
      scheduledContentEntityObject.data.title = imageTitle
      scheduledContentEntityObject.data.image_alt_text = imageAltText
      //only need the community identifier for Create, not Update
      if (!payload.typeId) {
        scheduledContentEntityObject.data.communityIdentifier =  payload.communityId
      }
      return scheduledContentEntityObject.save()
    })
  }

  protected updateProperty(property, value) {
    this.props.updateModal(ScheduledImageModalComponent.key, {
      name: property,
      value,
    })
    this.setState({ isDirty: true })
  }





  private getUploadErrorMsg(data: any) {
    let _isInValidImage: boolean = false
    if (data.imageWidth) {
      if (data.location.key && data.location.key.indexOf(ImageUploadType.BANNER) > -1) {
          _isInValidImage = data.imageWidth > IMAGE_MAX_WIDTH.BANNER
      }
      if (data.location.key && data.location.key.indexOf(ImageUploadType.SQUARE) > -1) {
        _isInValidImage = data.imageWidth > IMAGE_MAX_WIDTH.SQUARE
      }
    }
    if (_isInValidImage) {
      return  <div className= "Image-Upload-Size-ErrorMsg Image-Upload-Size-ErrorMsg-Label">
                {ImageUploadError.LargeImage}
              </div>
    }
  }

  private setSegmentCode = (code: any) => {
    this.updateProperty('community_partner_code', code.key)
  }

  private onChangeClick = () => {
    this.setState({
      isEditingImage: true,
      showImageUploadButtons: true
    })
    this.updateProperty('imageUrl', '')
    this.updateProperty('imageHeight', '')
    this.updateProperty('imageWidth', '')
  }



  private openImageSelectorModal = () => {
    this.setState({ showImageSelectorModal: true });
  }

  private closeImageSelectorModal = (key: string) => {
    this.setState({ showImageSelectorModal: false });
  }

  private handleImageSelection = (selectedImage: { url: string; imageWidth: number; imageHeight: number }) => {
    this.setState({
      sourceUrl: selectedImage.url,
      validUrl: Validation.validateImageUrl(selectedImage.url).valid,
      showImageUploadButtons: false,
      showImageSelectorModal: false
    });
    this.updateProperty('imageUrl', selectedImage.url);
    this.updateProperty('imageWidth', selectedImage.imageWidth);
    this.updateProperty('imageHeight', selectedImage.imageHeight);
  }

  private onImageError = () => {
    this.updateProperty('imageUrl', '');
    this.setState({
      validUrl: false,
      sourceUrl: '',
    })
  }

  private shouldNameDisabled = (data: any): boolean => {
    return (data.isCloneItem) ? false : (data.id && new Date(data.startDate) < new Date())
  }

  private setSegmentData = (configData: any) => {
    const community_partner_code = this.props.data[this.key].data.community_partner_code
    if (!community_partner_code && configData.length > 0) {
      this.updateProperty('community_partner_code', DEFAULT_COMMUNITY_PARTNER_CODES.ALL)
    }
  }

  private getSelectedLocation = () => {
    return this.props.data[ScheduledImageModalComponent.key].data.location.key
  }

  private generateAltText = (currentLocation = '') => {
    if(!this.props.data[ScheduledImageModalComponent.key].data.location){
      return ''
    }
    if(!currentLocation){
      currentLocation = this.getSelectedLocation()
    }
    const altTextKey = currentLocation.split(' ')[0].toLowerCase() + '_alt_text'
    return this.props.emailLayout[altTextKey]
  }



}
